import { type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Plus, Trash2 } from 'lucide-react'

type BaseRow = { id?: string; client_id?: string; _destroy?: boolean }

export type ChildColumn<Row extends BaseRow> = {
  key: keyof Row & string
  label: string
  type?: 'text' | 'number'
  placeholder?: string
  fullWidth?: boolean
  render?: (row: Row, update: (value: string) => void, updateRow: (partial: Partial<Row>) => void) => ReactNode
}

const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
}

type Props<Row extends BaseRow> = {
  title: string
  addLabel: string
  emptyText: string
  rows: Row[]
  onChange: (rows: Row[]) => void
  columns: ChildColumn<Row>[]
  blankRow: () => Row
  gridClassName?: string
  headerExtra?: ReactNode
}

export default function ChildRowsSection<Row extends BaseRow>({
  title,
  addLabel,
  emptyText,
  rows,
  onChange,
  columns,
  blankRow,
  gridClassName,
  headerExtra,
}: Props<Row>) {
  const visibleCount = rows.filter(row => !row._destroy).length
  const inlineColumns = columns.filter(column => !column.fullWidth)
  const fullWidthColumns = columns.filter(column => column.fullWidth)

  function addRow() {
    onChange([...rows, { ...blankRow(), client_id: crypto.randomUUID() }])
  }

  function updateRow(index: number, key: string, value: string) {
    onChange(rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)))
  }

  function mergeRow(index: number, partial: Partial<Row>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...partial } : row)))
  }

  function removeRow(index: number) {
    const row = rows[index]
    if (row.id) {
      onChange(rows.map((r, i) => (i === index ? { ...r, _destroy: true } : r)))
    } else {
      onChange(rows.filter((_, i) => i !== index))
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle>{title}</CardTitle>
            {headerExtra}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addRow}>
            <Plus className="mr-2 size-4" />
            {addLabel}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {visibleCount === 0 ? (
          <p className="rounded-xl border border-dashed border-black/10 py-6 text-center text-sm text-muted-foreground">
            {emptyText}
          </p>
        ) : (
          <div className="space-y-4">
            {rows.map((row, index) => {
              if (row._destroy) return null
              const renderField = (column: ChildColumn<Row>) => (
                <div key={column.key} className="space-y-2">
                  <Label className="text-xs text-muted-foreground">{column.label}</Label>
                  {column.render ? (
                    column.render(
                      row,
                      value => updateRow(index, column.key, value),
                      partial => mergeRow(index, partial),
                    )
                  ) : (
                    <Input
                      type={column.type ?? 'text'}
                      placeholder={column.placeholder}
                      value={(row[column.key] as string) ?? ''}
                      onChange={e => updateRow(index, column.key, e.target.value)}
                    />
                  )}
                </div>
              )
              return (
                <div key={row.id ?? row.client_id} className="space-y-4">
                  <div className="flex items-end gap-4">
                    <div className={`grid flex-1 gap-4 ${gridClassName ?? GRID_COLS[inlineColumns.length] ?? 'grid-cols-4'}`}>
                      {inlineColumns.map(renderField)}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="mb-0.5 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeRow(index)}
                      title="Remove"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  {fullWidthColumns.map(renderField)}
                </div>
              )
            })}
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              <Plus className="mr-2 size-4" />
              {addLabel}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
